import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LambdaService } from 'src/app/services/lambda.service';
import { UserPoolService } from 'src/app/services/user-pool.service';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { SkeletonModule } from 'primeng/skeleton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import { InputSwitchModule } from 'primeng/inputswitch';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { SocialChannels } from '../social-channels/social-channels';

export interface VirtualAgent {
  id: number;
  shopGuid: string;
  agent_code: string;
  description: string;
  shopId: number;
  shopPointId: number;
  shopPointName: string;
}

export interface ShopAgent {
  id: number;
  shopId: number;
  enabled: number;
  phone_number: string;
  agent_code: string;
  shopGuid: string;
  infobip_key: string;
  infobip_url: string;
}

@Component({
  selector: 'app-virtual-agents',
  imports: [
    CommonModule,
    CardModule,
    TagModule,
    DividerModule,
    SkeletonModule,
    ProgressSpinnerModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    FormsModule,
    DialogModule,
    CheckboxModule,
    ToastModule,
    InputSwitchModule,
    ConfirmPopupModule,
    SocialChannels
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './virtual-agents.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualAgentsComponent {
  virtualAgents = signal<VirtualAgent[]>([]);
  shopAgents = signal<ShopAgent[]>([]);
  filteredVirtualAgents = signal<VirtualAgent[]>([]);
  filteredShopAgents = signal<ShopAgent[]>([]);
  loading = signal<boolean>(false);
  loadingShopAgents = signal<boolean>(false);
  showSensitiveData = signal<{ [key: string]: boolean }>({});
  searchTerm = signal<string>('');
  shopAgentsSearchTerm = signal<string>('');
  store: any = {};

  showCreateAgentModal = signal<boolean>(false);
  creatingAgent = signal<boolean>(false);
  selectedVirtualAgent: VirtualAgent | null = null;

  showHumanAgentModal = signal<boolean>(false);
  loadingHumanAgents = signal<boolean>(false);
  humanAgents = signal<any[]>([]);
  filteredHumanAgents = signal<any[]>([]);
  humanAgentsSearchTerm = signal<string>('');
  selectedVirtualAgentForHuman: VirtualAgent | null = null;
  processingUserId = signal<string>('');

  showShopPointModal = signal<boolean>(false);
  loadingShopPoint = signal<boolean>(false);
  shopPointData = signal<any>(null);
  selectedVirtualAgentForShopPoint: VirtualAgent | null = null;

  newAgentForm = {
    shopId: 0,
    infobip_key: '',
    infobip_url: '',
    enabled: true,
    phone_number: '',
    agent_code: '',
    shopGuid: ''
  };

  constructor(
    private lambdaService: LambdaService,
    private messageService: MessageService,
    private userPoolService: UserPoolService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.getVirtualAgentConfig();
    this.getShopsAgents();
  }

  async getVirtualAgentConfig() {
    this.loading.set(true);
    try {
      const response = await this.lambdaService.getVirtualAgentConfig() as VirtualAgent[];
      this.virtualAgents.set(response);
      this.filteredVirtualAgents.set(response);
    } catch (error) {
      console.error('Error fetching virtual agent config:', error);
    } finally {
      this.loading.set(false);
    }
  }

  async getShopsAgents() {
    this.loadingShopAgents.set(true);
    try {
      const response = await this.lambdaService.getShopsAgents() as ShopAgent[];
      this.shopAgents.set(response);
      this.filteredShopAgents.set(response);
    } catch (error) {
      console.error('Error fetching shops agents:', error);
    } finally {
      this.loadingShopAgents.set(false);
    }
  }

  getShopData(shopId: any) {
    this.lambdaService
      .getShopData(shopId)
      .then((data: any) => {
        this.store = data.info;

        const infobip_api_host_uri = this.store.infobip_api_host_uri;
        const infobip_api_key = this.store.infobip_api_key;

        if (infobip_api_host_uri) {
          this.newAgentForm.infobip_url = infobip_api_host_uri;
        }
        if (infobip_api_key) {
          this.newAgentForm.infobip_key = infobip_api_key;
        }
      })
      .catch((error: Error) => {
        console.error('Error al obtener datos de la tienda:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron obtener los datos de la tienda'
        });
      })
      .finally(() => {
        setTimeout(() => {
        }, 500);
      });
  }

  async createShopAgent(agentId: number) {
    this.selectedVirtualAgent = this.virtualAgents().find(agent => agent.id === agentId) || null;

    if (this.selectedVirtualAgent) {
      this.newAgentForm = {
        shopId: this.selectedVirtualAgent.shopId,
        infobip_key: '',
        infobip_url: '',
        enabled: true,
        phone_number: '',
        agent_code: this.selectedVirtualAgent.agent_code,
        shopGuid: this.selectedVirtualAgent.shopGuid
      };

      this.getShopData(this.selectedVirtualAgent.shopId);

      this.showCreateAgentModal.set(true);
    }
  }

  closeCreateAgentModal() {
    this.showCreateAgentModal.set(false);
    this.selectedVirtualAgent = null;
    this.resetForm();
  }

  resetForm() {
    this.newAgentForm = {
      shopId: 0,
      infobip_key: '',
      infobip_url: '',
      enabled: true,
      phone_number: '',
      agent_code: '',
      shopGuid: ''
    };
  }

  async submitCreateAgent() {
    if (!this.isFormValid()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario incompleto',
        detail: 'Por favor completa todos los campos requeridos'
      });
      return;
    }

    this.creatingAgent.set(true);

    try {
      await this.lambdaService.createShopAgent(
        this.newAgentForm.shopId,
        this.newAgentForm.infobip_key,
        this.newAgentForm.infobip_url,
        this.newAgentForm.enabled,
        Number(this.newAgentForm.phone_number),
        this.newAgentForm.agent_code,
        this.newAgentForm.shopGuid
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Agente creado exitosamente',
        life: 4000
      });

      this.getShopsAgents();

      this.closeCreateAgentModal();

    } catch (error) {
      console.error('Error al crear agente:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo crear el agente',
        life: 6000
      });
    } finally {
      this.creatingAgent.set(false);
    }
  }

  isFormValid(): boolean {
    return !!(
      this.newAgentForm.shopId &&
      this.newAgentForm.infobip_key &&
      this.newAgentForm.infobip_url &&
      this.newAgentForm.phone_number &&
      this.newAgentForm.agent_code &&
      this.newAgentForm.shopGuid
    );
  }

  getAgentStatus(enabled: number) {
    return enabled === 1 ? 'Activo' : 'Inactivo';
  }

  getAgentStatusSeverity(enabled: number) {
    return enabled === 1 ? 'success' : 'secondary';
  }

  toggleSensitiveData(agentId: number, field: string) {
    const key = `${agentId}_${field}`;
    const current = this.showSensitiveData();
    this.showSensitiveData.set({
      ...current,
      [key]: !current[key]
    });
  }

  isSensitiveDataVisible(agentId: number, field: string): boolean {
    const key = `${agentId}_${field}`;
    return this.showSensitiveData()[key] || false;
  }

  maskSensitiveData(data: string): string {
    return '•'.repeat(Math.min(data.length, 20));
  }

  onSearchChange() {
    const term = this.searchTerm().toLowerCase();
    if (!term) {
      this.filteredVirtualAgents.set(this.virtualAgents());
      return;
    }

    const filtered = this.virtualAgents().filter(agent =>
      agent.description.toLowerCase().includes(term) ||
      agent.agent_code.toLowerCase().includes(term) ||
      agent.shopId.toString().includes(term) ||
      agent.shopPointName.toLowerCase().includes(term)
    );

    this.filteredVirtualAgents.set(filtered);
  }

  clearSearch() {
    this.searchTerm.set('');
    this.onSearchChange();
  }

  onShopAgentsSearchChange() {
    const term = this.shopAgentsSearchTerm().toLowerCase();
    if (!term) {
      this.filteredShopAgents.set(this.shopAgents());
      return;
    }

    const filtered = this.shopAgents().filter(agent =>
      agent.agent_code.toLowerCase().includes(term) ||
      agent.phone_number.includes(term) ||
      agent.shopId.toString().includes(term)
    );

    this.filteredShopAgents.set(filtered);
  }

  clearShopAgentsSearch() {
    this.shopAgentsSearchTerm.set('');
    this.onShopAgentsSearchChange();
  }

  async toggleAgentStatus(agent: ShopAgent) {
    if (!agent) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No se encontró el agente especificado'
      });
      return;
    }

    const currentStatus = agent.enabled;
    const newStatus = currentStatus === 1 ? 0 : 1;
    const isEnabled = newStatus === 1;
    const statusText = isEnabled ? 'activado' : 'desactivado';

    const updatedConfig = {
      id: agent.id,
      shopId: agent.shopId,
      enabled: isEnabled
    };

    try {
      let data: any;
      try {
        data = await this.lambdaService.updateVirtualAgentConfig(
          updatedConfig.id.toString(),
          updatedConfig.shopId.toString(),
          updatedConfig.enabled
        );
      } catch (firstError) {
        console.warn('Failed with first attempt, retrying:', firstError);
        data = await this.lambdaService.updateVirtualAgentConfig(
          updatedConfig.id.toString(),
          updatedConfig.shopId.toString(),
          updatedConfig.enabled
        );
      }

      if (data.status === 200 && data.msg === "success") {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: `Agente virtual ${agent.agent_code} ${statusText} correctamente`,
          life: 4000
        });

        agent.enabled = newStatus;

        const currentAgents = this.shopAgents();
        const updatedAgents = currentAgents.map(a =>
          a.id === agent.id ? { ...a, enabled: newStatus } : a
        );
        this.shopAgents.set(updatedAgents);
        this.filteredShopAgents.set(updatedAgents.filter(a =>
          this.shopAgentsSearchTerm() === '' ||
          a.agent_code.toLowerCase().includes(this.shopAgentsSearchTerm().toLowerCase()) ||
          a.phone_number.includes(this.shopAgentsSearchTerm()) ||
          a.shopId.toString().includes(this.shopAgentsSearchTerm())
        ));

      } else {
        throw new Error(`Respuesta inesperada del servidor: ${JSON.stringify(data)}`);
      }
    } catch (error) {
      console.error('Final error:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Error del servidor: ${error instanceof Error ? error.message : 'No se pudo actualizar la configuración del agente ' + agent.agent_code}`
      });
    }
  }

  async openHumanAgentModal(agentId: number) {

    this.selectedVirtualAgentForHuman = this.virtualAgents().find(agent => agent.id === agentId) || null;

    if (!this.selectedVirtualAgentForHuman) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No se encontró el agente virtual especificado'
      });
      return;
    }

    this.showHumanAgentModal.set(true);
    await this.loadHumanAgents();
  }

  async loadHumanAgents() {
    this.loadingHumanAgents.set(true);
    try {
      const users = await this.userPoolService.getUsers();

      this.humanAgents.set(users);
      this.filteredHumanAgents.set(users);

      if (users.length === 0) {
        this.messageService.add({
          severity: 'info',
          summary: 'Información',
          detail: 'No se encontraron usuarios en el User Pool'
        });
      }
    } catch (error) {
      console.error('Error al cargar usuarios del User Pool:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los usuarios del User Pool'
      });
    } finally {
      this.loadingHumanAgents.set(false);
    }
  }

  closeHumanAgentModal() {
    this.showHumanAgentModal.set(false);
    this.selectedVirtualAgentForHuman = null;
    this.humanAgents.set([]);
    this.filteredHumanAgents.set([]);
    this.humanAgentsSearchTerm.set('');
    this.processingUserId.set('');
  }

  onHumanAgentsSearchChange() {
    const term = this.humanAgentsSearchTerm().toLowerCase();
    if (!term) {
      this.filteredHumanAgents.set(this.humanAgents());
      return;
    }

    const filtered = this.humanAgents().filter(user =>
      user.username.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term) ||
      (user.attributes.name && user.attributes.name.toLowerCase().includes(term)) ||
      (user.attributes.family_name && user.attributes.family_name.toLowerCase().includes(term))
    );

    this.filteredHumanAgents.set(filtered);
  }

  clearHumanAgentsSearch() {
    this.humanAgentsSearchTerm.set('');
    this.onHumanAgentsSearchChange();
  }

  selectHumanAgent(user: any, event: Event) {

    if (!this.selectedVirtualAgentForHuman) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se ha seleccionado un agente virtual válido'
      });
      return;
    }

    const userFullName = this.getFullName(user);

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `¿Deseas asignar a ${userFullName} como agente humano para ${this.selectedVirtualAgentForHuman.description}?`,
      header: 'Confirmar Asignación',
      icon: 'pi pi-user-plus',
      acceptButtonStyleClass: 'p-button-success p-button-sm',
      rejectButtonStyleClass: 'p-button-outlined p-button-sm',
      acceptLabel: 'Sí, Asignar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.processHumanAgentCreation(user, userFullName);
      }
    });
  }

  async processHumanAgentCreation(user: any, userFullName: string) {
    try {
      this.processingUserId.set(user.username);

      const response = await this.lambdaService.updateShopAgent(
        this.selectedVirtualAgentForHuman!.shopId,
        this.selectedVirtualAgentForHuman!.shopPointId,
        user.username // use username as newAgentId
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Agente Humano Creado',
        detail: `${userFullName} ha sido asignado exitosamente como agente humano para ${this.selectedVirtualAgentForHuman!.description}`,
        life: 5000
      });

      await this.getShopsAgents();

      this.closeHumanAgentModal();

    } catch (error) {
      console.error('Error al crear agente humano:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error al Crear Agente',
        detail: `No se pudo asignar a ${userFullName} como agente humano: ${error instanceof Error ? error.message : 'Error desconocido'}`,
        life: 7000
      });
    } finally {
      this.processingUserId.set('');
    }
  }

  getObjectKeys(obj: any): string[] {
    return Object.keys(obj || {});
  }

  getObjectEntries(obj: any): [string, any][] {
    return Object.entries(obj || {});
  }

  getFullName(user: any): string {
    let attrObj: any = {};

    if (user.Attributes && Array.isArray(user.Attributes)) {
      user.Attributes.forEach((attr: any) => {
        if (attr.Name && attr.Value) {
          attrObj[attr.Name] = attr.Value;
        }
      });
    } else if (user.attributes && typeof user.attributes === 'object') {
      attrObj = user.attributes;
    }

    const givenName = attrObj.given_name || '';
    const familyName = attrObj.family_name || '';
    const name = attrObj.name || '';
    const email = attrObj.email || user.email || '';


    if (givenName && familyName) {
      const fullName = `${givenName} ${familyName}`;
      return fullName;
    }

    if (name) {
      return name;
    }

    if (givenName) {
      return givenName;
    }

    if (familyName) {
      return familyName;
    }

    const fallback = email || user.username || user.Username || 'Usuario sin nombre';
    return fallback;
  }

  getVisibleAttributes(attributes: any): [string, any][] {
    if (!attributes) return [];

    const hiddenAttributes = [
      'given_name',
      'family_name',
      'name',
      'email',
      'phone_number',
      'email_verified'
    ];

    return Object.entries(attributes).filter(([key]) =>
      !hiddenAttributes.includes(key)
    );
  }

  formatAttributeName(attributeName: string): string {
    const nameMap: { [key: string]: string } = {
      'sub': 'ID Usuario',
      'custom:shop_access': 'Acceso a Tienda',
      'custom:user_role': 'Rol de Usuario',
      'cognito:username': 'Username',
      'cognito:mfa_enabled': 'MFA Habilitado',
      'preferred_username': 'Nombre Preferido',
      'profile': 'Perfil',
      'website': 'Sitio Web',
      'picture': 'Foto de Perfil',
      'locale': 'Idioma',
      'zoneinfo': 'Zona Horaria',
      'birthdate': 'Fecha de Nacimiento',
      'gender': 'Género',
      'address': 'Dirección'
    };

    if (nameMap[attributeName]) {
      return nameMap[attributeName];
    }

    if (attributeName.startsWith('custom:')) {
      return attributeName.replace('custom:', '').replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    return attributeName.replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  async openShopPointModal(agentId: number) {
    this.selectedVirtualAgentForShopPoint = this.virtualAgents().find(agent => agent.id === agentId) || null;

    if (!this.selectedVirtualAgentForShopPoint) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No se encontró el agente virtual especificado',
        life: 4000
      });
      return;
    }

    if (this.humanAgents().length === 0) {
      await this.loadHumanAgents();
    }

    this.showShopPointModal.set(true);
    await this.loadShopPointData();
  }

  async loadShopPointData() {
    if (!this.selectedVirtualAgentForShopPoint) return;

    this.loadingShopPoint.set(true);
    try {
      const shopPointData = await this.lambdaService.getShopPointById(
        this.selectedVirtualAgentForShopPoint.shopPointId,
        this.selectedVirtualAgentForShopPoint.shopId
      );

      this.shopPointData.set(shopPointData);

    } catch (error) {
      console.error('Error al cargar datos del shop point:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los datos del shop point',
        life: 6000
      });
    } finally {
      this.loadingShopPoint.set(false);
    }
  }

  closeShopPointModal() {
    this.showShopPointModal.set(false);
    this.selectedVirtualAgentForShopPoint = null;
    this.shopPointData.set(null);
  }

  getShopPointAgents(): any[] {
    const data = this.shopPointData();
    if (!data) return [];

    if (data.shopAgentId && typeof data.shopAgentId === 'string') {
      // Limpiar y dividir los IDs
      const agentIds = data.shopAgentId
        .replace(/"/g, '')
        .split(',')
        .map((id: string) => id.trim())
        .filter((id: string) => id.length > 0);

      const usedAgents = new Set();

      return agentIds.map((agentId: string, index: number) => {
        const userPoolAgent = this.humanAgents().find(agent => {
          if (usedAgents.has(agent.username || agent.Username)) {
            return false;
          }

          const usernameMatch = agent.username === agentId || agent.Username === agentId;

          let subMatch = false;
          if (agent.Attributes && Array.isArray(agent.Attributes)) {
            const subAttribute = agent.Attributes.find((attr: any) => attr.Name === 'sub')?.Value;
            subMatch = subAttribute === agentId;
          } else if (agent.attributes && agent.attributes.sub) {
            subMatch = agent.attributes.sub === agentId;
          }

          const match = usernameMatch || subMatch;

          if (match) {
            usedAgents.add(agent.username || agent.Username);
            return true;
          }

          return false;
        });

        const fullName = userPoolAgent ? this.getFullName(userPoolAgent) : null;

        let email = '';
        let phone = '';

        if (userPoolAgent) {
          if (userPoolAgent.Attributes && Array.isArray(userPoolAgent.Attributes)) {
            email = userPoolAgent.Attributes.find((attr: any) => attr.Name === 'email')?.Value || '';
            phone = userPoolAgent.Attributes.find((attr: any) => attr.Name === 'phone_number')?.Value || '';
          } else if (userPoolAgent.attributes) {
            email = userPoolAgent.attributes.email || '';
            phone = userPoolAgent.attributes.phone_number || '';
          }

          email = email || userPoolAgent.email || '';
        }

        return {
          id: agentId,
          agentId: agentId,
          name: fullName || `Agente ${index + 1}`,
          agent_code: agentId,
          status: 1,
          role: 'Shop Agent',
          email: email,
          phone: phone,
          userPoolData: userPoolAgent
        };
      });
    }

    if (data.agents && Array.isArray(data.agents)) {
      return data.agents;
    }

    if (data.shopPointAgents && Array.isArray(data.shopPointAgents)) {
      return data.shopPointAgents;
    }

    if (data.shopPoint && data.shopPoint.agents && Array.isArray(data.shopPoint.agents)) {
      return data.shopPoint.agents;
    }

    return [];
  }

  jsonStringify(obj: any): string {
    return JSON.stringify(obj, null, 2);
  }

  confirmDeleteAgent(agent: any, event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `¿Estás seguro de eliminar al agente "${agent.name || agent.agentId}"?`,
      header: 'Confirmar Eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-text',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.deleteShopPointAgent(agent);
      }
    });
  }

  async deleteShopPointAgent(agent: any) {
    if (!this.selectedVirtualAgentForShopPoint) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se ha seleccionado un shop point'
      });
      return;
    }

    try {
      const shopId = this.selectedVirtualAgentForShopPoint.shopId;
      const shopPointId = this.selectedVirtualAgentForShopPoint.shopPointId;
      const agentIdToRemove = agent.agentId || agent.id;

      await this.lambdaService.deleteShopPointAgent(shopId, shopPointId, agentIdToRemove);

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: `Agente "${agent.name || agent.agentId}" eliminado correctamente`
      });

      await this.loadShopPointData();

    } catch (error) {
      console.error('Error eliminando agente:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo eliminar el agente. Inténtelo de nuevo.'
      });
    }
  }

}
